//WorkFlowEditorContainer.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import WorkFlowEditor from "./WorkFlowEditor";
import {
    WorkFlowEditorType,
    appActivity,
    ApprovalType,
    RejectionType,
    FormData as FormItemData,
    approvalTypeOptions,
    rejectionTypeOptions,
    ToUser,
    MailRecipientTargets
} from "../../data/WorkFlowEditorType";
import { getWorkflowEditorData, getCreateNewWorkflowData } from "../../../../service/WorkFlowEditorService";
import { WorkflowEditorResponse } from "../../../../type/response/WorkFlowEditorResponse";
import { WorkflowEditorRequest } from "../../../../type/request/WorkflowEditorRequest";
import { axiosRequest } from "../../../../service/AxiosService";
import { RoomPreferencesTwoTone } from "@mui/icons-material";

/**
 * ワークフローエディターコンテナ
 * - URLパラメータのidに応じて新規作成・既存編集のデータを取得し、WorkFlowEditorに渡す
 * - アクティビティ・フォームアイテム・メール設定などの各種変更ハンドラーを管理する
 */
const WorkFlowEditorContainer: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [responseData, setResponseData] = useState<WorkflowEditorResponse | null>(null);
    const [workflowData, setWorkflowData] = useState<WorkFlowEditorType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const location = useLocation();
    const { reusedWorkflowData, reuseMode } = location.state || {};


    // 初期レンダリング時にワークフローデータを取得する（新規作成・既存編集・複製で分岐）
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                if (id === "new") {
                    const newData = await getCreateNewWorkflowData();

                    if (newData) {
                        let finalData = newData;

                        if (reusedWorkflowData) {
                            if (reuseMode === "formOnly") {
                                finalData = {
                                    ...newData,
                                    formItems: reusedWorkflowData.formItems,
                                    selectTypeList: newData.selectTypeList,
                                    selectTemplateList: newData.selectTemplateList,
                                    selectApproverList: newData.selectApproverList,
                                    selectReporterList: newData.selectReporterList,
                                };
                            } else {
                                finalData = {
                                    ...reusedWorkflowData,

                                };
                            }
                        }

                        setWorkflowData(finalData);
                    } else {
                        setError("新規workflowの初期データ取得に失敗しました");
                    }
                }

                else if (id) {
                    // 既存workflowの場合
                    const data = await getWorkflowEditorData(id);
                    if (data) {
                        setResponseData(data.responseData);
                        setWorkflowData(data.formattedData);
                    } else {
                        setError("既存workflowのデータ取得に失敗しました");
                    }
                } else {
                    // idが存在しない場合（不正な遷移など）
                    setError("IDが指定されていません");
                }
            } catch (error) {
                console.error("データ取得エラー:", error);
                setError("データの取得中にエラーが発生しました");
            } finally {
                setLoading(false); // 終了時にローディング解除
            }
        };

        fetchData();
    }, [id, reuseMode, reusedWorkflowData]);


    if (loading) return <div>読み込み中...</div>;
    if (error) return <div>{error}</div>;
    if (!workflowData) return null;

    const activities = workflowData.appActivityList || [];

    // アクティビティ一覧をworkflowDataに反映するヘルパー
    const setActivities = (updated: appActivity[]) => {
        setWorkflowData(prev => (prev ? { ...prev, appActivityList: updated } : prev));
    };

    // フォームデータの即時反映
    const formItems = workflowData.formItems || [];

    // フォームアイテム一覧をworkflowDataに反映するヘルパー
    const setFormItems = (newFormItems: FormItemData[]) => {
        setWorkflowData(prev => (prev ? { ...prev, formItems: newFormItems } : prev));
    };
    //既存ワークフローを修正する場合は、responseデータをもとに、requestを構成する
    const buildRequestFromEditor = (
        editor: WorkFlowEditorType,
        base: WorkflowEditorResponse,
        isDraft: boolean = false

    ): WorkflowEditorRequest => {

        return {
            application: {
                id: base.application.id,
                code: base.application.code,
                version: "", // バックエンドで生成
                name: editor.name ?? base.application.name,
                description: editor.description ?? base.application.description,
                isDeleted: false,
                isDraft: isDraft
            },
            applicationType: {
                id: editor.type.id,
                name: editor.type.typeName
            },
            applicationActivities: editor.appActivityList?.map((a) => ({
                id: a.applicationActivityId,
                activityOrder: a.order,
                activityName: a.appActivityName,
                defaultConfig: a.defaultConfig,
                mailTemplate: a.mailTemplate,
                mailFormat: a.mailFormat,
                reporterList: a.reporterList,
                template: a.template
            })) ?? [],
            selectTypeList: [],
            selectTemplateList: [],
            selectMailTemplateList: [],
            selectUserList: [],
            formItems: editor.formItems.map(item => ({
                id: item.formId,
                itemOrder: item.itemOrder ?? 0,
                label: item.label,
                type: item.type,
                isRequired: item.isRequired,
                defaultValue: item.defaultValue,
                selectItemList: item.selectItemList
            }))
        };
    };

    //新規作成の場合、ベースデータは空
    const buildRequestForNewWorkflow = (editor: WorkFlowEditorType, isDraft: boolean = false): WorkflowEditorRequest => {
        return {
            application: {
                id: "",
                code: "", // バックエンドで生成
                version: "", // バックエンドで生成
                name: editor.name ?? "",
                description: editor.description ?? "",
                isDeleted: false,
                isDraft: isDraft
            },
            applicationType: {
                id: editor.type.id,
                name: editor.type.typeName
            },
            applicationActivities: editor.appActivityList?.map((a) => ({
                id: "",
                activityOrder: a.order,
                activityName: a.appActivityName,
                defaultConfig: a.defaultConfig,
                mailTemplate: a.mailTemplate,
                mailFormat: a.mailFormat,
                reporterList: a.reporterList,
                template: a.template
            })) ?? [],
            selectTypeList: [],
            selectTemplateList: [],
            selectMailTemplateList: [],
            selectUserList: [],
            formItems: editor.formItems.map(item => ({
                id: "",
                itemOrder: item.itemOrder ?? 0,
                label: item.label,
                type: item.type,
                isRequired: item.isRequired,
                defaultValue: item.defaultValue,
                selectItemList: item.selectItemList
            }))
        };
    };


    // 現在のワークフローを複製して新規作成画面へ遷移する（全体複製またはフォームのみ複製）
    const handleReuseWorkflow = (mode: "full" | "formOnly") => {
        if (!workflowData) return;

        const reused = {
            ...workflowData,
        };

        navigate("/edit/new", {
            state: {
                reusedWorkflowData: reused,
                reuseMode: mode,
            },
        });
    };

    //アクティビティ削除
    const handleDelete = (id: string) => {
        const updated = activities
            .filter(activity => activity.applicationActivityId !== id)
            .map((activity, idx) => ({
                ...activity,
                order: idx + 1,
            }));
        setActivities(updated);
    };
    //アクティビティ名変更
    const handleChangeName = (id: string, value: string) => {
        setActivities(
            activities.map(activity =>
                activity.applicationActivityId === id
                    ? { ...activity, appActivityName: value }
                    : activity
            )
        );
    };

    //テンプレート変更
    const handleChangeTemplate = (id: string, value: string) => {
        const selectedTemplate = workflowData.selectTemplateList?.find(t => t.templateName === value);
        if (!selectedTemplate) return;
        setActivities(
            activities.map(activity => {
                if (activity.applicationActivityId !== id) return activity;
                const newDefaultConfig =
                    selectedTemplate.code === "1"
                        ? {
                            approval: { type: "ANY_APPROVED" as ApprovalType, details: null },
                            rejection: { type: "ANY_DENIED" as RejectionType, details: null }
                        }
                        :
                        selectedTemplate.code === "2"
                            ? { mail: { recipientTargets: { applicant: true, approver: true, referent: true } } }
                            : {};

                return {
                    ...activity,
                    template: selectedTemplate,
                    defaultConfig: newDefaultConfig
                };
            })
        );
    };

    // メールテンプレート変更
    const handleChangeMailTemplate = (id: string, value: string) => {
        const selectedMailTemplate = workflowData.selectMailTemplateList?.find(
            (t) => t.name === value
        );
        setWorkflowData((prev) => {
            if (!prev) return prev;
            const updatedActivities = prev.appActivityList?.map((activity) =>
                activity.applicationActivityId === id
                    ? { ...activity, mailTemplate: selectedMailTemplate }
                    : activity
            );
            return { ...prev, appActivityList: updatedActivities };
        });
    };

    // メールフォーマット変更
    const handleChangeMailFormat = (
        id: string,
        subject: string,
        body: string,
        toUsers: ToUser[]
    ) => {
        setWorkflowData((prev) => {
            if (!prev) return prev;
            const updatedActivities = prev.appActivityList?.map((activity) =>
                activity.applicationActivityId === id
                    ? {
                        ...activity,
                        mailFormat: {
                            id: activity.mailFormat?.id || "",
                            subject: subject,
                            body: body,
                            toUsers: toUsers.length > 0 ? toUsers : undefined,
                        },
                    }
                    : activity
            );
            return { ...prev, appActivityList: updatedActivities };
        });
    };

    const handleChangeMailRecipientTargets = (id: string, targets: MailRecipientTargets) => {
        setWorkflowData(prev => {
            if (!prev) return prev;
            const updatedActivities = prev.appActivityList?.map(activity =>
                activity.applicationActivityId === id
                    ? {
                        ...activity,
                        defaultConfig: {
                            ...(activity.defaultConfig ?? {}),
                            mail: {
                                ...(activity.defaultConfig?.mail ?? {}),
                                recipientTargets: targets,
                            },
                        },
                    }
                    : activity
            );
            return { ...prev, appActivityList: updatedActivities };
        });
    };

    //報告者変更
    const handleChangeReporter = (id: string, reporterIds: string[]) => {
        const selectedReporters = workflowData.selectReporterList?.filter(r =>
            reporterIds.includes(r.id)
        ) ?? [];
        setWorkflowData(prev => {
            if (!prev) return prev;
            const updatedActivities = prev.appActivityList?.map(activity =>
                activity.applicationActivityId === id
                    ? { ...activity, reporterList: selectedReporters }
                    : activity
            );
            return {
                ...prev,
                appActivityList: updatedActivities
            };
        });
    };
    //承認条件変更
    const handleChangeApprovalCondition = (id: string, value: string) => {
        setActivities(
            activities.map(activity =>
                activity.applicationActivityId === id
                    ? {
                        ...activity,
                        defaultConfig: {
                            ...activity.defaultConfig,
                            approval: { type: value as ApprovalType, details: null }
                        }
                    }
                    : activity
            )
        );
    };
    //却下条件変更
    const handleChangeRejectionCondition = (id: string, value: string) => {
        setActivities(
            activities.map(activity =>
                activity.applicationActivityId === id
                    ? {
                        ...activity,
                        defaultConfig: {
                            ...activity.defaultConfig,
                            rejection: { type: value as RejectionType, details: null }
                        }
                    }
                    : activity
            )
        );
    };
    //割合変更
    const handleChangePercentage = (id: string, percent: number, isApproval: boolean) => {
        setActivities(
            activities.map(activity =>
                activity.applicationActivityId === id
                    ? {
                        ...activity,
                        defaultConfig: {
                            ...activity.defaultConfig,
                            ...(isApproval
                                ? {
                                    approval: {
                                        ...activity.defaultConfig.approval!,
                                        details: { requiredPercent: percent }
                                    }
                                }
                                : {
                                    rejection: {
                                        ...activity.defaultConfig.rejection!,
                                        details: { requiredPercent: percent }
                                    }
                                })
                        }
                    }
                    : activity
            )
        );
    };
    //照査者変更
    const handleChangeConditionalApprover = (
        id: string,
        approverIds: string[],
        isApproval: boolean
    ) => {
        const selectedApprovers = workflowData.selectApproverList?.filter(a =>
            approverIds.includes(a.id)
        ) ?? [];

        setActivities(
            activities.map(activity =>
                activity.applicationActivityId === id
                    ? {
                        ...activity,
                        defaultConfig: {
                            ...activity.defaultConfig,
                            ...(isApproval
                                ? {
                                    approval: {
                                        ...activity.defaultConfig.approval!,
                                        details: { userList: selectedApprovers }
                                    }
                                }
                                : {
                                    rejection: {
                                        ...activity.defaultConfig.rejection!,
                                        details: { userList: selectedApprovers }
                                    }
                                })
                        }
                    }
                    : activity
            )
        );
    };

    //修正後データを保存
    const handleSaveEditData = async () => {
        if (!workflowData) return;
        const request =
            id === "new"
                ? buildRequestForNewWorkflow(workflowData)
                : responseData
                    ? buildRequestFromEditor(workflowData, responseData)
                    : null;

        if (!request) return;

        try {
            const endpoint = id === "new"
                ? "/workflowEditor/saveNewData" // 新規ワークフロー保存
                : "/workflowEditor/saveEditData";//既存ワークフロー修正後保存
            await axiosRequest(endpoint, "POST", {
                data: request,
            });

            navigate("/OperationCompleted", {
                state: {
                    action: "ワークフロー保存",
                },
            });
        } catch (error) {
            console.error("保存エラー:", error);
        }
    };

    //一時保存データを保存
    const handleTempSave = async () => {
        if (!workflowData) return;
        const request =
            id === "new"
                ? buildRequestForNewWorkflow(workflowData, true) // isDraft: true
                : responseData
                    ? buildRequestFromEditor(workflowData, responseData, true) // isDraft: true
                    : null;

        if (!request) return;

        try {
            const endpoint = id === "new"
                ? "/workflowEditor/saveNewData" // 新規ワークフロー一時保存
                : "/workflowEditor/saveEditData";//既存ワークフロー一時保存
            const response: any = await axiosRequest(endpoint, "POST", {
                data: request,
            });

            navigate("/OperationCompleted", {
                state: {
                    action: "ワークフロー一時保存",
                },
            });
        } catch (error) {
            console.error("一時保存エラー:", error);
        }
    };
    return (
        <div>
            <WorkFlowEditor
                workflow={{
                    type: workflowData.type || { id: "", typeName: "" },
                    name: workflowData.name || "",
                    description: workflowData.description || ""
                }}
                activities={activities}
                formItems={formItems}
                setFormItems={setFormItems}
                selectTypeList={workflowData.selectTypeList || []}
                selectReporterList={workflowData.selectReporterList || []}
                selectToUserList={workflowData.selectToUserList || []}
                setWorkflow={(wf) => setWorkflowData(prev => (prev ? { ...prev, ...wf } : prev))}
                setActivities={setActivities}
                templateList={workflowData.selectTemplateList || []}
                mailTemplateList={workflowData.selectMailTemplateList || []}
                approverList={workflowData.selectApproverList || []}
                reporterList={workflowData.selectReporterList || []}
                approvalTypeOptions={approvalTypeOptions}
                rejectionTypeOptions={rejectionTypeOptions}
                isNewWorkflow={id === "new"}
                handleChangeType={(newType) =>
                    setWorkflowData(prev => (prev ? { ...prev, type: newType } : prev))
                }
                handleDelete={handleDelete}
                handleChangeName={handleChangeName}
                handleChangeTemplate={handleChangeTemplate}
                handleChangeMailTemplate={handleChangeMailTemplate}
                handleChangeMailFormat={handleChangeMailFormat}
                handleChangeReporter={handleChangeReporter}
                handleChangeApprovalCondition={handleChangeApprovalCondition}
                handleChangeRejectionCondition={handleChangeRejectionCondition}
                handleChangePercentage={handleChangePercentage}
                handleChangeConditionalApprover={handleChangeConditionalApprover}
                handleSaveEditData={handleSaveEditData}
                handleTempSave={handleTempSave}
                handleReuseWorkflow={handleReuseWorkflow}
                handleChangeMailRecipientTargets={handleChangeMailRecipientTargets}
            />
        </div>

    );
};

export default WorkFlowEditorContainer;